// pdfTheme.js — Light-mode forcing helpers for html2canvas PDF capture.
//
// html2canvas captures screen rendering, not print media. When the user is in
// dark mode, `.dark` on <html> causes dark: Tailwind variants to fire on every
// off-screen container mounted to document.body, producing dark-background PDFs.
//
// These two helpers are the single correct fix point. Call them around every
// html2canvas capture sequence — including the moment any off-screen container
// is appended to document.body, not just the html2canvas() call itself.

// ---------------------------------------------------------------------------
// Internal scrim helpers — no exports, no external dependencies
// ---------------------------------------------------------------------------

const SCRIM_ATTR = "data-pdf-capture-scrim";

function installScrim() {
  let scrim = document.querySelector(`[${SCRIM_ATTR}]`);
  if (scrim) {
    // Already mounted by a concurrent call — bump the refcount and return.
    const count = parseInt(scrim.getAttribute(SCRIM_ATTR), 10) || 1;
    scrim.setAttribute(SCRIM_ATTR, String(count + 1));
    return;
  }

  scrim = document.createElement("div");
  scrim.setAttribute(SCRIM_ATTR, "1");
  // CRITICAL: tell html2canvas to skip the scrim entirely. The scrim is
  // position:fixed with a dark navy background at max z-index — it's there
  // to mask the live UI flicker during the theme flip. Without this
  // attribute, html2canvas clones it into the capture iframe and (because
  // it's fixed-position) it overlays the captured target, producing
  // entirely-black pages or slices in the resulting PDF.
  scrim.setAttribute("data-html2canvas-ignore", "true");

  Object.assign(scrim.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    background: "rgb(15, 23, 42)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    fontFamily: "Inter, system-ui, sans-serif",
    color: "rgb(248, 250, 252)",
    pointerEvents: "auto",
  });

  // Spinner — a single CSS keyframe animation injected once.
  if (!document.getElementById("pdf-scrim-keyframes")) {
    const style = document.createElement("style");
    style.id = "pdf-scrim-keyframes";
    style.textContent =
      "@keyframes pdf-scrim-spin { to { transform: rotate(360deg); } }";
    document.head.appendChild(style);
  }

  const spinner = document.createElement("div");
  Object.assign(spinner.style, {
    width: "28px",
    height: "28px",
    border: "3px solid rgb(71, 85, 105)",
    borderTopColor: "rgb(248, 250, 252)",
    borderRadius: "50%",
    animation: "pdf-scrim-spin 0.75s linear infinite",
  });

  const label = document.createElement("span");
  Object.assign(label.style, {
    fontSize: "14px",
    letterSpacing: "0.01em",
    opacity: "0.9",
  });
  label.textContent = "Generating PDF…";

  scrim.appendChild(spinner);
  scrim.appendChild(label);
  document.body.appendChild(scrim);
}

function removeScrim() {
  const scrim = document.querySelector(`[${SCRIM_ATTR}]`);
  if (!scrim) return;
  const count = parseInt(scrim.getAttribute(SCRIM_ATTR), 10) || 1;
  if (count > 1) {
    scrim.setAttribute(SCRIM_ATTR, String(count - 1));
  } else {
    scrim.remove();
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Synchronously removes `.dark` from <html> and returns a restore function.
 * Safe to call when `.dark` is already absent.
 * Scrim-free — suitable for synchronous callers that manage their own UI.
 *
 * @returns {() => void} Call this to restore the original theme state.
 */
export function forceLightForPdf() {
  const html = document.documentElement;
  const hadDark = html.classList.contains("dark");
  if (hadDark) html.classList.remove("dark");
  return () => {
    if (hadDark) html.classList.add("dark");
  };
}

/**
 * Wraps an async function so that <html> is forced into light mode for the
 * entire duration, then restored (even if asyncFn throws).
 *
 * A full-viewport opaque scrim is installed BEFORE the dark→light flip so the
 * user never sees the live UI reflow. The scrim is removed after the theme is
 * restored. Concurrent calls share a single scrim via refcounting.
 *
 * Usage:
 *   const canvas = await withLightTheme(() => html2canvas(el, opts));
 *
 * For sequences that mount DOM before calling html2canvas, wrap the whole
 * sequence:
 *   const result = await withLightTheme(async () => {
 *     document.body.appendChild(container);
 *     await twoFrames();
 *     return html2canvas(container, opts);
 *   });
 *
 * @template T
 * @param {() => Promise<T>} asyncFn
 * @returns {Promise<T>}
 */
export async function withLightTheme(asyncFn) {
  // 1. Install (or refcount-bump) the scrim first.
  installScrim();

  // 2. Yield one animation frame so the scrim paints before the theme flips.
  await new Promise((resolve) => requestAnimationFrame(resolve));

  // 3. Flip the theme.
  const restore = forceLightForPdf();

  // 4. Yield TWO more frames so the browser recalculates CSS custom properties
  //    (--background, --foreground, etc.) and Tailwind dark: variants after the
  //    class removal.  One frame is not enough on some browsers — the style
  //    recalculation for CSS variables can lag by one additional compositing
  //    frame, causing html2canvas to capture stale (dark) computed colours.
  await new Promise((resolve) => requestAnimationFrame(resolve));
  await new Promise((resolve) => requestAnimationFrame(resolve));

  try {
    return await asyncFn();
  } finally {
    // 5. Restore theme, then remove scrim.
    restore();
    removeScrim();
  }
}
